import { Grid, Typography } from '@mui/material';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { BasePage } from './bases/BasePage';
import { EmployeeCutActivityCard } from '@components/Employee/EmployeeCutActivityCard';
import { EmployeeInfoCard } from '@components/Employee/EmployeeInfoCard';
import { EmployeeRankCard } from '@components/Employee/EmployeeRankCard';
import { DatePickerWithChips } from '@components/DatePickerWithChips';
import { EmployeeMaterialsCard } from '@components/Employee/EmployeeMaterialsCard';
import { useState } from 'react';
import { useQuery } from '@hooks/useQuery';
import { useStyles } from './styles/EmployeeDetails';
import { AppRoutes } from '@consts/routes';
import { DatePreset, datePresetFns } from '@utils/dates';
import { useNavAssert } from '@hooks/useNavAssert';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';

export const EmployeeDetails = () => {
    const classes = useStyles();
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const queryParams = useQuery();
    const params = {
        startDate: queryParams.get('startDate'),
        endDate: queryParams.get('endDate'),
        employeeEmail: queryParams.get('email')!,
        locationId: +queryParams.get('locationId')!,
    };

    const decodedEmployeeEmail = decodeURIComponent(params.employeeEmail);

    // ? If a valid date range was provided in the query params, init with that range
    const paramsDateRange =
        params.startDate && params.endDate
            ? {
                  startDate: decodeURIComponent(params.startDate),
                  endDate: decodeURIComponent(params.endDate),
              }
            : null;

    const [datePreset, setDatePreset] = useState<DatePreset | null>(paramsDateRange ? null : DatePreset.LastSevenDays);
    const [dateRange, setDateRange] = useState<{
        startDate?: string;
        endDate?: string;
    }>(paramsDateRange || datePresetFns[DatePreset.LastSevenDays]);

    // ? Cannot search a user without valid employee email & locationID
    // todo: Navigate back to employee report
    const isValidEmployee = Boolean(params.employeeEmail && params.locationId);
    useNavAssert(isValidEmployee, AppRoutes.Employees, 'Unable to find employee - invalid email or location ID.');
    useNavAssert(
        role.locations.scope === '*' || role.locations.scope.includes(params.locationId),
        AppRoutes.Insights,
        'Insufficient permissions to view this employee.'
    );

    return (
        <BasePage>
            <BreadcrumbSubBar />

            <div style={{ marginTop: '180px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <Typography component='h1' fontWeight='bold' fontSize='24px'>
                    Employee Details
                </Typography>

                <DatePickerWithChips
                    disabled={!isValidEmployee}
                    dateRange={dateRange}
                    onDateChange={setDateRange}
                    datePreset={datePreset}
                    onDatePresetChange={setDatePreset}
                />

                {isValidEmployee && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                            <Grid container height='100%'>
                                <Grid item xs={12} sm={6} md={12} lg={12} xl={12} className={classes.infoCardCell}>
                                    <EmployeeInfoCard
                                        employeeEmail={decodedEmployeeEmail}
                                        locationId={params.locationId}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={12} lg={12} xl={12}>
                                    <EmployeeRankCard
                                        employeeEmail={decodedEmployeeEmail}
                                        locationId={params.locationId}
                                        {...dateRange}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                            <EmployeeMaterialsCard
                                employeeEmail={params.employeeEmail}
                                locationId={params.locationId}
                                {...dateRange}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <EmployeeCutActivityCard
                                employeeEmail={params.employeeEmail}
                                locationId={params.locationId}
                                {...dateRange}
                            />
                        </Grid>
                    </Grid>
                )}
            </div>
        </BasePage>
    );
};
